import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCircle2, AlertTriangle, XCircle, Info, ArrowRight, RefreshCw, CheckCheck, Sparkles } from 'lucide-react';
import {
  fetchRecentNotifications,
  markNotificationRead,
  markAllNotificationsRead
} from '../services/notificationService';
import { getAuthRedirectPath } from '../utils/authRedirect';

const severityConfig = {
  success: {
    icon: CheckCircle2,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    accentClass: 'bg-emerald-500',
    cardClass: 'border-emerald-200/80 dark:border-emerald-500/20 bg-emerald-50/70 dark:bg-emerald-500/10'
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-600 dark:text-amber-400',
    accentClass: 'bg-amber-500',
    cardClass: 'border-amber-200/80 dark:border-amber-500/20 bg-amber-50/70 dark:bg-amber-500/10'
  },
  error: {
    icon: XCircle,
    iconClass: 'text-red-600 dark:text-red-400',
    accentClass: 'bg-red-500',
    cardClass: 'border-red-200/80 dark:border-red-500/20 bg-red-50/70 dark:bg-red-500/10'
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-600 dark:text-blue-400',
    accentClass: 'bg-blue-500',
    cardClass: 'border-blue-200/80 dark:border-blue-500/20 bg-blue-50/70 dark:bg-blue-500/10'
  }
};

const heroShellClass = 'relative mb-4 overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 p-4 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl sm:p-5';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroIconPanelClass = 'border border-primary-100 bg-white/70 text-primary-700 shadow-sm ring-1 ring-primary-100 dark:border-white/10 dark:bg-white/[0.08] dark:text-white dark:ring-white/10 dark:shadow-none';

const formatTimestamp = (value, fallback = 'Just now') => {
  const ts = Number(value || 0);
  if (!Number.isFinite(ts) || ts <= 0) return fallback;
  const dt = new Date(ts);
  if (Number.isNaN(dt.getTime())) return fallback;
  return dt.toLocaleString();
};

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState('');
  const [processingAll, setProcessingAll] = useState(false);

  const uid = user?.uid || '';
  const unreadCount = useMemo(() => items.filter((item) => !item.isRead).length, [items]);

  const loadNotifications = useCallback(async () => {
    if (!uid) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchRecentNotifications(uid, 60);
      setItems(data);
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleOpen = async (item) => {
    if (!item) return;
    try {
      if (!item.isRead) {
        setProcessingId(item.id);
        await markNotificationRead(uid, item.id);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
      }
    } finally {
      setProcessingId('');
      const route = item?.action?.route || '/orders';
      navigate(route);
    }
  };

  const handleMarkAllRead = async () => {
    if (!uid || unreadCount <= 0) return;
    setProcessingAll(true);
    try {
      await markAllNotificationsRead(uid, items);
      setItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
    } finally {
      setProcessingAll(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to={getAuthRedirectPath()} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-4xl px-4">
        <div className={heroShellClass}>
          <div className={heroGlowClass} />
          <div className="relative flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${heroIconPanelClass}`}>
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-black text-white ring-2 ring-white dark:ring-gray-950">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <div className={`mb-1 ${heroChipClass}`}>
                  <Sparkles className="h-3 w-3 text-amber-300" />
                  Live updates
                </div>
                <h1 className="truncate text-2xl font-black leading-tight sm:text-3xl">
                  {t('notifications.title', 'Notifications')}
                </h1>
                <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                  {unreadCount > 0
                    ? t('notifications.unread_count', { count: unreadCount }, `${unreadCount} unread`)
                    : t('notifications.all_caught_up', 'All caught up')}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={loadNotifications}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl transition hover:bg-white dark:hover:bg-white/[0.14] ${heroIconPanelClass}`}
                aria-label={t('notifications.refresh', 'Refresh')}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleMarkAllRead}
                disabled={processingAll || unreadCount === 0}
                className="inline-flex h-10 items-center justify-center gap-1.5 rounded-2xl bg-primary-600 px-3 text-xs font-black text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-100"
              >
                <CheckCheck className="h-4 w-4" />
                <span className="hidden sm:inline">{processingAll ? t('notifications.updating', 'Updating...') : t('notifications.mark_all_read', 'Mark all read')}</span>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2.5">
            {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-20 rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-300">
              <Bell className="w-6 h-6" />
            </div>
            <p className="font-bold text-gray-600 dark:text-gray-300">{t('notifications.empty', 'No notifications yet.')}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((item) => {
              const config = severityConfig[item.severity] || severityConfig.info;
              const Icon = config.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleOpen(item)}
                  className={`group relative w-full overflow-hidden rounded-2xl border p-3 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${config.cardClass} ${item.isRead ? 'opacity-85' : 'ring-1 ring-primary-300/40 dark:ring-primary-700/40'}`}
                >
                  <span className={`absolute inset-y-0 left-0 w-1 ${config.accentClass}`} />
                  <div className="flex items-start gap-3 pl-1">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm ring-1 ring-black/5 dark:bg-gray-950/40 dark:ring-white/10">
                      <Icon className={`w-4 h-4 ${config.iconClass}`} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-black text-gray-950 dark:text-white">{item.title}</h3>
                          <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{item.message}</p>
                        </div>
                        <span className="shrink-0 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                          {formatTimestamp(item.createdAt, t('notifications.just_now', 'Just now'))}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${item.isRead ? 'bg-gray-200/70 text-gray-500 dark:bg-white/10 dark:text-gray-400' : 'bg-primary-600 text-white dark:bg-primary-500'}`}>
                          {item.isRead ? t('notifications.read', 'Read') : t('notifications.new', 'New')}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs font-black text-primary-600 transition group-hover:translate-x-0.5 dark:text-primary-300">
                          {processingId === item.id ? t('notifications.opening', 'Opening...') : (item?.action?.label || t('notifications.open', 'Open'))}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
